import React from 'react';
import { Field, Input, Textarea, Select, Button, Card, SaveBar, move } from './ui';
import useDraft from './useDraft';

const ICON_NAMES = ['Beer', 'Target', 'Newspaper', 'Trophy', 'Users', 'Anchor', 'Crown', 'UtensilsCrossed'];

function Section({ title, note, children }) {
  return (
    <Card className="p-4">
      <h3 className="mb-1 text-sm font-bold uppercase tracking-wide text-slate-900">{title}</h3>
      {note && <p className="mb-3 text-xs text-slate-500">{note}</p>}
      <div className="grid gap-3 sm:grid-cols-2">{children}</div>
    </Card>
  );
}

export default function SettingsEditor({ stored }) {
  const { draft, setDraft, dirty, saving, error, save, reset } = useDraft('settings', stored);

  // set a nested value, e.g. put('hero', 'kicker', value)
  const put = (group, key, value) =>
    setDraft((d) => ({ ...d, [group]: { ...(d[group] || {}), [key]: value } }));

  const text = (group, key, label, hint) => (
    <Field label={label} hint={hint}>
      <Input value={draft?.[group]?.[key] ?? ''} onChange={(e) => put(group, key, e.target.value)} />
    </Field>
  );

  const area = (group, key, label, hint) => (
    <Field label={label} hint={hint} className="sm:col-span-2">
      <Textarea value={draft?.[group]?.[key] ?? ''} onChange={(e) => put(group, key, e.target.value)} />
    </Field>
  );

  const philosophy = draft?.philosophy || [];
  const categories = draft?.contact?.categories || [];

  return (
    <div className="space-y-4">
      <Section title="Zaglavlje novina" note="Sitni tekst iznad naslova.">
        {text('masthead', 'city', 'Grad')}
        {text('masthead', 'issue', 'Godina / broj')}
        {text('masthead', 'price', 'Cijena')}
        {text('masthead', 'tagline', 'Podnaslov ispod imena')}
      </Section>

      <Section title="Udarna vijest">
        {text('hero', 'kicker', 'Oznaka', 'Npr. Udarna Vijest')}
        {text('hero', 'locations', 'Lokacije')}
        {text('hero', 'headlineTop', 'Naslov — prvi red')}
        {text('hero', 'headlineBottom', 'Naslov — drugi red', 'Prikazuje se plavim kurzivom.')}
        {area('hero', 'lead1', 'Prvi odlomak', 'Prvo slovo postaje veliki inicijal.')}
        {area('hero', 'lead2', 'Drugi odlomak')}
      </Section>

      <Section title="Bočni stupac">
        {text('sidebar', 'seaTitle', 'Naslov — stanje mora')}
        {text('sidebar', 'seaText', 'Tekst — stanje mora')}
        {text('sidebar', 'wantedTitle', 'Naslov — traži se')}
        {text('sidebar', 'wantedText', 'Tekst — traži se')}
        {text('sidebar', 'wantedCode', 'Šifra')}
        {text('sidebar', 'quote', 'Citat dana')}
        {text('sidebar', 'quoteAuthor', 'Potpis citata')}
      </Section>

      <Card className="p-4">
        <h3 className="mb-3 text-sm font-bold uppercase tracking-wide text-slate-900">Riječ uredništva</h3>
        <div className="space-y-3">
          {philosophy.map((item, i) => (
            <div key={i} className="grid gap-2 rounded-md border border-slate-200 p-3 sm:grid-cols-[140px_1fr_auto]">
              <Field label="Ikona">
                <Select
                  value={item.icon}
                  onChange={(e) => setDraft((d) => ({
                    ...d, philosophy: d.philosophy.map((x, xi) => (xi === i ? { ...x, icon: e.target.value } : x)),
                  }))}
                >
                  {ICON_NAMES.map((n) => <option key={n}>{n}</option>)}
                </Select>
              </Field>
              <div className="space-y-2">
                <Field label="Naslov">
                  <Input
                    value={item.title}
                    onChange={(e) => setDraft((d) => ({
                      ...d, philosophy: d.philosophy.map((x, xi) => (xi === i ? { ...x, title: e.target.value } : x)),
                    }))}
                  />
                </Field>
                <Field label="Tekst">
                  <Textarea
                    value={item.desc}
                    onChange={(e) => setDraft((d) => ({
                      ...d, philosophy: d.philosophy.map((x, xi) => (xi === i ? { ...x, desc: e.target.value } : x)),
                    }))}
                  />
                </Field>
              </div>
              <div className="flex flex-col gap-1">
                <Button className="!px-2 !py-1" onClick={() => setDraft((d) => ({ ...d, philosophy: move(d.philosophy, i, i - 1) }))}>↑</Button>
                <Button className="!px-2 !py-1" onClick={() => setDraft((d) => ({ ...d, philosophy: move(d.philosophy, i, i + 1) }))}>↓</Button>
                <Button variant="danger" className="!px-2 !py-1" onClick={() => setDraft((d) => ({ ...d, philosophy: d.philosophy.filter((_, xi) => xi !== i) }))}>✕</Button>
              </div>
            </div>
          ))}
          <Button onClick={() => setDraft((d) => ({ ...d, philosophy: [...(d.philosophy || []), { icon: 'Newspaper', title: '', desc: '' }] }))}>
            + Stupac
          </Button>
        </div>
      </Card>

      <Section title="Ostali naslovi">
        {text('roster', 'season', 'Sezona (uz "Igrački Kadar")')}
        {text('gallery', 'footnote', 'Napomena ispod galerije')}
      </Section>

      <Card className="p-4">
        <h3 className="mb-1 text-sm font-bold uppercase tracking-wide text-slate-900">Pisma čitatelja</h3>
        <p className="mb-3 text-xs text-slate-500">Obrazac na dnu stranice.</p>
        <div className="grid gap-3 sm:grid-cols-2">
          {text('contact', 'title', 'Naslov')}
          {text('contact', 'subtitle', 'Podnaslov')}
          <Field label="Webhook URL" hint="Kamo se šalju poruke (Make.com)." className="sm:col-span-2">
            <Input value={draft?.contact?.webhookUrl ?? ''} onChange={(e) => put('contact', 'webhookUrl', e.target.value)} />
          </Field>
          <Field label="Kategorije" className="sm:col-span-2">
            <div className="space-y-2">
              {categories.map((c, i) => (
                <div key={i} className="flex gap-2">
                  <Input
                    value={c}
                    onChange={(e) => put('contact', 'categories', categories.map((x, xi) => (xi === i ? e.target.value : x)))}
                  />
                  <Button variant="danger" className="!px-2 !py-1" onClick={() => put('contact', 'categories', categories.filter((_, xi) => xi !== i))}>✕</Button>
                </div>
              ))}
              <Button onClick={() => put('contact', 'categories', [...categories, ''])}>+ Kategorija</Button>
            </div>
          </Field>
        </div>
      </Card>

      <Section title="Podnožje">
        {text('footer', 'publisher', 'Nakladnik')}
        {text('footer', 'redakcija', 'Redakcija')}
        {text('footer', 'phone', 'Telefon')}
        {text('footer', 'copyright', 'Copyright')}
      </Section>

      <SaveBar dirty={dirty} saving={saving} error={error} onSave={save} onReset={reset} />
    </div>
  );
}
